import { useState } from 'react';
import { Col, Row } from 'components/Grid';
import { Typography as T } from 'components';
import { BasicButton } from 'components/Button';
import Modal from 'components/Modal';
import useCopyToClipboard from '../../../Hooks/useCopyToClipboard';
import {
  useGetApiKeySettings,
  useRegenerateApiKey,
} from '../../../api-calls/external-api-settings.queries';
import getApiDocsUrl from '../../../helpers/get-api-docs-url';
import * as S from './style';

const ApiSettings = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const { copied, copyToClipboard } = useCopyToClipboard();

  const { data: settings, isLoading, error } = useGetApiKeySettings();
  const {
    mutate: regenerateKey,
    isLoading: isRegenerating,
    error: regenerateError,
  } = useRegenerateApiKey();

  const handleGenerateClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmGenerate = () => {
    regenerateKey(
      {},
      {
        onSuccess: (data) => {
          setNewKey(data.key);
          setShowConfirmModal(false);
        },
      }
    );
  };

  const handleCopyKey = () => {
    if (newKey) {
      copyToClipboard(newKey);
    }
  };

  const handleDismissNewKey = () => {
    setNewKey(null);
  };

  if (isLoading) {
    return (
      <Row inner jc="center">
        <Col w={[4, 12, 12]}>
          <T.H3>Loading...</T.H3>
        </Col>
      </Row>
    );
  }

  if (error) {
    return (
      <Row inner jc="center">
        <Col w={[4, 12, 12]}>
          <T.H3 color="error">Error loading API settings</T.H3>
          <T.P>{error.message}</T.P>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row mb={4} inner jc="space-between" ai="center">
        <Col w={[4, 12, 12]}>
          <T.H1 mb={3}>External API Settings</T.H1>
        </Col>
      </Row>

      {newKey && (
        <Row mb={4}>
          <Col w={[4, 12, 12]}>
            <S.NewKeyAlert>
              <T.H3 mb={2}>New API Key Generated</T.H3>
              <T.P mb={2}>
                Copy this key now. You won&apos;t be able to see it again!
              </T.P>
              <S.NewKeyValue>{newKey}</S.NewKeyValue>
              <S.ButtonRow>
                <BasicButton variant="secondary" onClick={handleCopyKey}>
                  {copied ? 'Copied!' : 'Copy Key'}
                </BasicButton>
                <BasicButton variant="tertiary" onClick={handleDismissNewKey}>
                  I&apos;ve copied it
                </BasicButton>
              </S.ButtonRow>
            </S.NewKeyAlert>
          </Col>
        </Row>
      )}

      <Row>
        <Col w={[4, 12, 12]}>
          <S.Card>
            {settings?.hasKey ? (
              <>
                <T.H3 mb={2}>Current API Key</T.H3>
                <S.KeyDisplay>{settings.keyPreview}</S.KeyDisplay>
                <S.InfoText>
                  Created: {new Date(settings.createdAt).toLocaleDateString()}
                </S.InfoText>
                <S.InfoText>Created by: {settings.createdBy}</S.InfoText>
                <S.ButtonRow>
                  <BasicButton
                    variant="secondary"
                    onClick={handleGenerateClick}
                    loading={isRegenerating}
                  >
                    Regenerate Key
                  </BasicButton>
                </S.ButtonRow>
              </>
            ) : (
              <>
                <T.P mb={4}>No API key has been generated yet.</T.P>
                <BasicButton
                  variant="secondary"
                  onClick={handleGenerateClick}
                  loading={isRegenerating}
                >
                  Generate API Key
                </BasicButton>
              </>
            )}

            {regenerateError && (
              <T.P color="error" mt={3}>
                {regenerateError.message || 'Failed to generate API key'}
              </T.P>
            )}
          </S.Card>
        </Col>
      </Row>

      <Row mt={4}>
        <Col w={[4, 12, 12]}>
          <S.Card>
            <T.H3 mb={2}>API Documentation</T.H3>
            <T.P mb={3}>
              View interactive API documentation with example requests and
              responses.
            </T.P>
            <BasicButton
              variant="primary"
              onClick={() => window.open(getApiDocsUrl(), '_blank')}
            >
              Open API Documentation
            </BasicButton>
          </S.Card>
        </Col>
      </Row>

      <Modal visible={showConfirmModal} setIsModalVisible={setShowConfirmModal}>
        <Row>
          <Col w={[4, 12, 12]}>
            <T.P size="med" weight="bold" color="white">
              {settings?.hasKey ? 'Regenerate API Key?' : 'Generate API Key?'}
            </T.P>
            <T.P size="regular" color="white" mt={4} mb={4}>
              {settings?.hasKey
                ? 'This will revoke the current API key. Any systems using the old key will no longer be able to authenticate.'
                : 'This will generate a new API key for external use.'}
            </T.P>

            <BasicButton
              variant="secondary"
              onClick={handleConfirmGenerate}
              loading={isRegenerating}
              mt={4}
            >
              {settings?.hasKey ? 'Yes, Regenerate' : 'Yes, Generate'}
            </BasicButton>
            <BasicButton
              variant="tertiary"
              onClick={() => setShowConfirmModal(false)}
              mt={2}
            >
              Cancel
            </BasicButton>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ApiSettings;
